import { forwardRef, memo, useImperativeHandle, type Dispatch, type SetStateAction } from 'react';
import router, { useRouter } from 'next/router';
import { Reorder, AnimatePresence } from 'framer-motion';

import Loader from '@atoms/Atom/Loader';
import Image from '@atoms/Image';
import useFonts from '@lib/hooks/useFonts';
import useNotificationTimer from '@lib/hooks/useNotificationTimer ';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import { defaultColor } from '@components/Presentation/constants';
import { type Interaction } from '@components/Presentation/types';

import FantasticFansItem from './FantasticFansItem';
import { useFantasticFans } from './FantasticFans/useFantasticFans';
import type { FantasticFansFilter } from './FantasticFans/types';

export interface FantasticFansHandle {
  stopCurrentFeature: () => void;
}

interface FantasticFansProps {
  streamId?: string;
  currentRosFeature?: Partial<Interaction>;
  setShowNotification?: Dispatch<SetStateAction<boolean>>;
  isSandbox?: boolean;
  isTrackModal?: boolean;
}

const FantasticFans = forwardRef<FantasticFansHandle, FantasticFansProps>(({
  streamId,
  currentRosFeature,
  setShowNotification,
  isSandbox = false,
  isTrackModal = false,
}, ref) => {
  const joinType = useRouter().query?.joinType;

  const filter = (currentRosFeature?.setting?.fantasticFansShowWinners
    || 'all') as FantasticFansFilter;
  const colors = currentRosFeature?.color || defaultColor;

  const { fans, sentinelRef, isLoading, hasNextPage } = useFantasticFans({
    streamId,
    featureId: currentRosFeature?.id,
    filter,
  });

  useNotificationTimer(currentRosFeature, fans, setShowNotification);

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');

  const calculatedFontSize = useFontSizeCalculator(
    currentRosFeature?.setting?.ffFontSize ? Number(currentRosFeature.setting.ffFontSize) : 1.2,
    1.8,
    'rem',
  );
  const computedFontSize = isTrackModal ? '20px' : calculatedFontSize;

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}/ros`, query: joinType ? { joinType } : {} });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  return (
    <div className="flex w-full h-full relative">
      <div
        id="scrollableDiv"
        style={{
          cursor: 'default',
          fontFamily,
          ...(isSandbox && { height: 'calc(100vh - 400px)' }),
        }}
        className="flex-1 scrollable-container no-scrollbar max-w-6xl mx-auto flex flex-col pb-4 overflow-y-auto!"
      >
        {isLoading && <Loader showMessages={false} />}

        {!isLoading && fans.length > 0 && (
          <>
            <Reorder.Group
              axis="y"
              values={fans.map((fan) => fan.id)}
              onReorder={() => { }}
              className="space-y-2"
              layout
            >
              <AnimatePresence>
                <FantasticFansItem
                  isTrackModal={isTrackModal}
                  fans={fans}
                  colors={colors}
                  fontSize={computedFontSize}
                  fontFamily={fontFamily}
                />
              </AnimatePresence>
            </Reorder.Group>
            {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
          </>
        )}

        {!isLoading && fans.length === 0 && (
          <div className="absolute m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
            <div className="flex flex-col items-center">
              <Image src="/images/chat.svg" />
              <div className="mt-8">
                <div className="flex justify-center items-center">
                  <span className="font-medium text-white text-center text-lg leading-6">Nobody has commented</span>
                </div>
                <div className="flex justify-center items-center">
                  <span className="font-medium text-white text-center text-lg leading-6">yet in the chat</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

FantasticFans.displayName = 'FantasticFans';

export default memo(FantasticFans);
